import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Input,
  Table,
  Form,
  Typography,
  message,
  Icon,
} from "antd";
import { ColumnProps } from "antd/lib/table";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import styles from "./QuarterDetailModal.module.css";

const { TextArea } = Input;
const { Text } = Typography;

interface IQuarterDetailModalProps {
  visible: boolean;
  year: number;
  quarter: number;
  onClose: () => void;
  onOpenStatistics: () => void;
  onExpertEstimationSaved?: (year: number, quarter: number, value: number) => void;
  enableExpertEditing?: boolean;
}

interface ICommentRow {
  key: string;
  text: string;
  date: string;
  quarterInfo?: string;
}

interface IQuarterProgressState {
  currentProgressPercent: number | null;
  expertProgressPercent: number | null;
  comments: ICommentRow[];
  loading: boolean;
}

const QuarterDetailModal: React.FC<IQuarterDetailModalProps> = ({
  visible,
  year,
  quarter,
  onClose,
  onOpenStatistics,
  onExpertEstimationSaved,
  enableExpertEditing = true,
}) => {
  const appContext = React.useContext<IAppContext>(AppContext);
  const [state, setState] = useState<IQuarterProgressState>({
    currentProgressPercent: null,
    expertProgressPercent: null,
    comments: [],
    loading: false,
  });
  const [newExpertValue, setNewExpertValue] = useState<string>("");
  const [newComment, setNewComment] = useState<string>("");
  const [savingExpert, setSavingExpert] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const services = appContext?.services;

  const loadData = async () => {
    if (!services?.sviDashboardService) {
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const [progressResponse, commentsResponse] = await Promise.all([
        services.sviDashboardService.getQuarterProgress(year, quarter),
        services.sviDashboardService.getAllComments(),
      ]);
      const progressPayload = progressResponse.data;
      const commentsPayload = commentsResponse.data;
      const progressData =
        progressPayload?.success && progressPayload?.data
          ? progressPayload.data
          : null;
      const allComments =
        commentsPayload?.success && Array.isArray(commentsPayload?.data)
          ? commentsPayload.data
          : [];
      const rows: ICommentRow[] = allComments.map((c, index) => ({
        key: `${index}-${c.date}-${c.text?.slice(0, 10)}`,
        text: c.text,
        date: c.date,
        quarterInfo:
          c.year != null && c.quarter != null
            ? `Q${c.quarter} ${c.year}`
            : undefined,
      }));
      setState({
        currentProgressPercent: progressData?.currentProgressPercent ?? null,
        expertProgressPercent: progressData?.expertProgressPercent ?? null,
        comments: rows,
        loading: false,
      });
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false }));
      message.error("Failed to load data. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Error loading quarter data", err);
    }
  };

  useEffect(() => {
    if (visible) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, year, quarter]);

  const handleSaveExpert = async () => {
    if (!services?.sviDashboardService) {
      return;
    }
    const trimmed = newExpertValue.trim();
    if (!trimmed) {
      message.error("Please enter an expert progress estimation.");
      return;
    }
    const numeric = Number(trimmed);
    if (Number.isNaN(numeric)) {
      message.error("Please enter a numeric percentage (e.g. 85).");
      return;
    }
    if (numeric < 0 || numeric > 100) {
      message.error("Please enter a value between 0 and 100.");
      return;
    }
    try {
      setSavingExpert(true);
      await services.sviDashboardService.addExpertEstimation(
        year,
        quarter,
        numeric,
      );
      message.success("Expert progress estimation saved.");
      setNewExpertValue("");
      onExpertEstimationSaved?.(year, quarter, numeric);
      await loadData();
    } catch (err) {
      message.error("Saving expert estimation failed. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Error saving expert estimation", err);
    } finally {
      setSavingExpert(false);
    }
  };

  const handleSaveComment = async () => {
    const trimmedComment = newComment.trim();
    if (!services?.sviDashboardService || !trimmedComment) {
      return;
    }
    try {
      setSavingComment(true);
      await services.sviDashboardService.addQuarterComment(
        year,
        quarter,
        trimmedComment,
      );
      message.success("Comment saved.");
      setNewComment("");
      await loadData();
    } catch (err) {
      message.error("Saving comment failed. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Error saving quarter comment", err);
    } finally {
      setSavingComment(false);
    }
  };

  const columns: ColumnProps<ICommentRow>[] = [
    {
      title: "Comments",
      dataIndex: "text",
      key: "text",
    },
    {
      title: "Quarter",
      dataIndex: "quarterInfo",
      key: "quarterInfo",
      width: 100,
      render: (val: string) => val || "-",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 140,
      render: (val: string) =>
        val ? new Date(val).toLocaleDateString() : "-",
    },
  ];

  const expertProgressText =
    state.expertProgressPercent != null
      ? `${state.expertProgressPercent}%`
      : "Not set";

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable={true}
      closeIcon={<Icon type="close" style={{ color: '#ff4d4f' }} />}
      footer={null}
      title={`Scene Quarter ${quarter}, ${year} - Expert Progress Estimation`}
      width={820}
      destroyOnClose={false}
      wrapClassName="quarter-detail-modal"
    >
      <div className={styles.container}>
        <div className={styles.body}>
          <div className={styles.summaryRow}>
            {enableExpertEditing && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Expert estimation</span>
                <span className={styles.summaryValue}>{expertProgressText}</span>
              </div>
            )}
          </div>

          <div className={styles.sections}>
            {enableExpertEditing && (
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <div>
                    <div className={styles.sectionTitle}>
                      Update expert progress estimation
                    </div>
                    <div className={styles.sectionSubtitle}>
                      Enter a value in percent between 0 and 100.
                    </div>
                  </div>
                </div>
                <div className={styles.expertRow}>
                  <Form layout="inline" style={{ flex: 1 }}>
                    <Form.Item label="Expert estimation (%)">
                      <Input
                        className={styles.expertInput}
                        value={newExpertValue}
                        onChange={(e) => setNewExpertValue(e.target.value)}
                        placeholder="e.g. 85"
                      />
                    </Form.Item>
                  </Form>
                  <div className={styles.expertActions}>
                    <Button
                      type="primary"
                      className={styles.primaryGoldButton}
                      onClick={handleSaveExpert}
                      loading={savingExpert}
                      disabled={savingExpert}
                    >
                      Save estimation
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <div className={styles.commentsHeaderRow}>
                  <div className={styles.sectionTitle}>Comments</div>
                  <div className={styles.commentsCount}>
                    {state.comments.length}{" "}
                    {state.comments.length === 1 ? "entry" : "entries"}
                  </div>
                </div>
              </div>
              <Table<ICommentRow>
                size="small"
                pagination={false}
                loading={state.loading}
                columns={columns}
                dataSource={state.comments}
                locale={{ emptyText: "No comments yet" }}
              />
            </div>

            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <div>
                  <div className={styles.sectionTitle}>Add new comment</div>
                  <div className={styles.sectionSubtitle}>
                    Please mention the year and quarter you are commenting on.
                  </div>
                </div>
              </div>
              <div className={styles.newCommentRow}>
                <TextArea
                  rows={3}
                  placeholder="Type your comment here…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <div className={styles.newCommentActions}>
                  <Button
                    type="primary"
                    className={styles.primaryGoldButton}
                    onClick={handleSaveComment}
                    loading={savingComment}
                    disabled={savingComment || !newComment.trim()}
                  >
                    Save comment
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.statsFooter}>
              <div className={styles.statsDescription}>
                View the consolidated overview of expert and current progress
                estimations across all quarters.
              </div>
              <Button
                type="primary"
                className={styles.primaryGoldButton}
                onClick={onOpenStatistics}
              >
                General Statistics
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default QuarterDetailModal;

